import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Code,
  Languages,
  Megaphone,
  Monitor,
  Heart,
  BookOpen,
  Cloud,
  Database,
  Smartphone,
  Palette,
  Briefcase,
  Users,
  Plus,
  MoreVertical,
  Edit3,
  Trash2,
  Archive,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import { skillApi } from '../../services/api';
import { SkillType, Skill } from '../../types/hr';
import { useToast } from '../../hooks/use-toast';

// Available icons for categories
const AVAILABLE_ICONS = [
  { name: 'Code', component: Code, label: 'Programming' },
  { name: 'Heart', component: Heart, label: 'Soft Skills' },
  { name: 'Languages', component: Languages, label: 'Languages' },
  { name: 'Megaphone', component: Megaphone, label: 'Marketing' },
  { name: 'Monitor', component: Monitor, label: 'Technology' },
  { name: 'Cloud', component: Cloud, label: 'Cloud' },
  { name: 'Database', component: Database, label: 'Database' },
  { name: 'Smartphone', component: Smartphone, label: 'Mobile' },
  { name: 'Palette', component: Palette, label: 'Design' },
  { name: 'Briefcase', component: Briefcase, label: 'Business' },
  { name: 'Users', component: Users, label: 'People' },
  { name: 'BookOpen', component: BookOpen, label: 'Learning' }
];

interface CategoryFormData {
  name: string;
  description: string;
  icon: string;
}

interface SkillFormData {
  name: string;
  description: string;
  typeId: string;
}

const SkillsLibrary: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch skill types
  const { data: skillTypes = [], isLoading: typesLoading, error: typesError } = useQuery({
    queryKey: ['skillTypes'],
    queryFn: () => skillApi.types.getAll(),
  });

  // Fetch skills
  const { data: skills = [], isLoading: skillsLoading, error: skillsError } = useQuery({
    queryKey: ['skills'],
    queryFn: () => skillApi.skills.getAll({}),
  });

  // Modal states
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false);

  // Form states
  const [editingCategory, setEditingCategory] = useState<SkillType | null>(null);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryFormData>({
    name: '',
    description: '',
    icon: 'BookOpen'
  });
  const [skillForm, setSkillForm] = useState<SkillFormData>({
    name: '',
    description: '',
    typeId: ''
  });

  // Mutations for skill types
  const createTypeMutation = useMutation({
    mutationFn: skillApi.types.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTypes'] });
      setIsCategoryModalOpen(false);
      toast({ title: 'Category created successfully' });
      setCategoryForm({ name: '', description: '', icon: 'BookOpen' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => skillApi.types.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTypes'] });
      setIsCategoryModalOpen(false);
      setEditingCategory(null);
      toast({ title: 'Category updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutations for skills
  const createSkillMutation = useMutation({
    mutationFn: skillApi.skills.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      setIsSkillModalOpen(false);
      toast({ title: 'Skill created successfully' });
      setSkillForm({ name: '', description: '', typeId: '' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateSkillMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => skillApi.skills.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      setIsSkillModalOpen(false);
      setEditingSkill(null);
      toast({ title: 'Skill updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete skill mutation
  const deleteSkillMutation = useMutation({
    mutationFn: (id: string) => skillApi.skills.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      toast({ title: 'Skill deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete category (skill type) mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) => skillApi.types.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skillTypes'] });
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      toast({ title: 'Category deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const getSkillTypeIcon = (iconName?: string) => {
    const iconInfo = AVAILABLE_ICONS.find(icon => icon.name === iconName);
    return iconInfo ? iconInfo.component : BookOpen;
  };

  // Category management functions
  const openCategoryModal = (category?: SkillType) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        icon: category.icon || 'BookOpen'
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        icon: 'BookOpen'
      });
    }
    setIsCategoryModalOpen(true);
  };

  const handleCategorySubmit = () => {
    if (!categoryForm.name.trim()) return;

    if (editingCategory) {
      updateTypeMutation.mutate({
        id: editingCategory.id,
        data: {
          name: categoryForm.name,
          description: categoryForm.description,
          icon: categoryForm.icon,
        },
      });
    } else {
      createTypeMutation.mutate({
        name: categoryForm.name,
        description: categoryForm.description,
        icon: categoryForm.icon,
      });
    }
  };

  const handleDeleteCategory = (category: SkillType) => {
    const skillCount = skills.filter(s => (s as any).skill_type_id === category.id || s.typeId === category.id).length;
    const message = skillCount > 0
      ? `Are you sure you want to delete "${category.name}"? This will also delete ${skillCount} skill(s) in this category.`
      : `Are you sure you want to delete "${category.name}"?`;
    
    if (confirm(message)) {
      deleteCategoryMutation.mutate(category.id);
    }
  };

  // Skill management functions
  const openSkillModal = (skill?: Skill, defaultTypeId?: string) => {
    if (skill) {
      setEditingSkill(skill);
      setSkillForm({
        name: skill.name,
        description: skill.description || '',
        typeId: (skill as any).skill_type_id || skill.typeId
      });
    } else {
      setEditingSkill(null);
      setSkillForm({
        name: '',
        description: '',
        typeId: defaultTypeId || skillTypes[0]?.id || ''
      });
    }
    setIsSkillModalOpen(true);
  };

  const handleSkillSubmit = () => {
    if (!skillForm.name.trim() || !skillForm.typeId) return;

    if (editingSkill) {
      updateSkillMutation.mutate({
        id: editingSkill.id,
        data: {
          name: skillForm.name,
          description: skillForm.description,
          skill_type_id: skillForm.typeId,
        },
      });
    } else {
      createSkillMutation.mutate({
        name: skillForm.name,
        description: skillForm.description,
        skill_type_id: skillForm.typeId,
      });
    }
  };

  const toggleSkillStatus = (skill: Skill) => {
    updateSkillMutation.mutate({
      id: skill.id,
      data: { is_active: !((skill as any).is_active ?? skill.isActive) },
    });
  };

  const handleDeleteSkill = (skill: Skill) => {
    if (confirm(`Are you sure you want to delete "${skill.name}"?`)) {
      deleteSkillMutation.mutate(skill.id);
    }
  };

  // Loading state
  if (typesLoading || skillsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading skills...</span>
      </div>
    );
  }

  // Error state
  if (typesError || skillsError) {
    return (
      <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg">
        <p className="font-semibold">Error loading skills</p>
        <p className="text-sm mt-1">{(typesError || skillsError)?.message || 'Please try again later'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage skill categories and individual skills for employee assessments</h1>
          
        </div>
        <Button
          onClick={() => openCategoryModal()}
          className="flex items-center  bg-[#1e3a5f] hover:bg-[#143A62] text-white m-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Category</span>
        </Button>
      </div>

      {/* Skills Grid */}
      <div className="space-y-6">
        {skillTypes.map(skillType => {
          const typeSkills = skills.filter(skill => (skill as any).skill_type_id === skillType.id || skill.typeId === skillType.id);
          const activeSkills = typeSkills.filter(skill => ((skill as any).is_active ?? skill.isActive));
          const IconComponent = getSkillTypeIcon(skillType.icon);

          return (
            <div key={skillType.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <IconComponent className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{skillType.name}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant="outline">{activeSkills.length} active skills</Badge>
                      {typeSkills.length > activeSkills.length && (
                        <Badge variant="secondary">{typeSkills.length - activeSkills.length} archived</Badge>
                      )}
                    </div>
                    {skillType.description && (
                      <p className="text-sm text-gray-600 mt-1">{skillType.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="soft"
                    className='flex items-center   text-[#1e3a5f] m-2'
                    size="sm"
                    onClick={() => openSkillModal(undefined, skillType.id)}
                   
                  >
                    <Plus className="h-3 w-3" />
                    <span>Add Skill</span>
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="soft" size="sm" className="px-2">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openCategoryModal(skillType)}>
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit Category
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteCategory(skillType)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Category
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {typeSkills.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {typeSkills.map(skill => (
                    <div key={skill.id} className={`border rounded-lg p-4 transition-all ${((skill as any).is_active ?? skill.isActive)
                        ? 'border-gray-200 bg-white hover:bg-gray-50'
                        : 'border-gray-100 bg-gray-50'
                      }`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h4 className={`font-medium ${((skill as any).is_active ?? skill.isActive) ? 'text-gray-900' : 'text-gray-500'}`}>
                            {skill.name}
                          </h4>
                          <div className={`w-2 h-2 rounded-full ${((skill as any).is_active ?? skill.isActive) ? 'bg-secondary' : 'bg-gray-400'
                            }`}></div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openSkillModal(skill)}>
                              <Edit3 className="h-4 w-4 mr-2" />
                              Edit Skill
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleSkillStatus(skill)}>
                              {((skill as any).is_active ?? skill.isActive) ? (
                                <>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive Skill
                                </>
                              ) : (
                                <>
                                  <Eye className="h-4 w-4 mr-2" />
                                  Activate Skill
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteSkill(skill)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Skill
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {skill.description && (
                        <p className={`text-sm ${((skill as any).is_active ?? skill.isActive) ? 'text-gray-600' : 'text-gray-500'}`}>
                          {skill.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No skills in this category yet.</p>
                  <Button
                    variant="ghost"
                    onClick={() => openSkillModal(undefined, skillType.id)}
                    className="mt-2 text-secondary hover:text-secondary/90"
                  >
                    Add your first skill
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Category Modal */}
      <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Plus className="h-5 w-5 text-secondary" />
              <span>{editingCategory ? 'Edit Category' : 'Add New Category'}</span>
            </DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update the skill category details' : 'Create a new skill category for organizing skills'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category-name">Category Name</Label>
              <Input
                id="category-name"
                placeholder="e.g., Cloud Platforms, Project Management"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-icon">Icon</Label>
              <Select
                value={categoryForm.icon}
                onValueChange={(value) => setCategoryForm(prev => ({ ...prev, icon: value }))}
              >
                <SelectTrigger>
                  <SelectValue>
                    {(() => {
                      const selected = AVAILABLE_ICONS.find(i => i.name === categoryForm.icon);
                      if (!selected) return null;
                      const IconComp = selected.component;
                      return (
                        <div className="flex items-center space-x-2">
                          <IconComp className="h-4 w-4" />
                          <span>{selected.label}</span>
                        </div>
                      );
                    })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_ICONS.map(icon => {
                    const IconComp = icon.component;
                    return (
                      <SelectItem key={icon.name} value={icon.name}>
                        <div className="flex items-center space-x-2">
                          <IconComp className="h-4 w-4" />
                          <span>{icon.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category-description">Description</Label>
              <Textarea
                id="category-description"
                placeholder="Brief description of what skills this category includes..."
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="soft" onClick={() => setIsCategoryModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCategorySubmit}
              disabled={!categoryForm.name.trim()}
              className="bg-[var(--color-secondary)] text-white shadow hover:bg-[var(--color-secondary-hover)]"
            >
              {editingCategory ? 'Update Category' : 'Add Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Skill Modal */}
      <Dialog open={isSkillModalOpen} onOpenChange={setIsSkillModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Plus className="h-5 w-5 text-secondary" />
              <span>{editingSkill ? 'Edit Skill' : 'Add New Skill'}</span>
            </DialogTitle>
            <DialogDescription>
              {editingSkill ? 'Update the skill details' : 'Add a new skill to the library'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="skill-name">Skill Name</Label>
              <Input
                id="skill-name"
                placeholder="e.g., Agile Methodology, Time Management"
                value={skillForm.name}
                onChange={(e) => setSkillForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="skill-category">Category</Label>
              <Select
                value={skillForm.typeId}
                onValueChange={(value) => setSkillForm(prev => ({ ...prev, typeId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {skillTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="skill-description">Description</Label>
              <Textarea
                id="skill-description"
                placeholder="Brief description of this skill..."
                value={skillForm.description}
                onChange={(e) => setSkillForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="soft" onClick={() => setIsSkillModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSkillSubmit}
              disabled={!skillForm.name.trim() || !skillForm.typeId}
              className="bg-[var(--color-secondary)] hover:bg-[var(--color-secondary-hover)] space-x-2"
            >
              {editingSkill ? 'Update Skill' : 'Add Skill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SkillsLibrary;